import React, { useState, useCallback } from 'react'
import Popup from 'devextreme-react/popup'
import Button from 'devextreme-react/button'
import TextBox from 'devextreme-react/text-box'
import FilterBuilder, { CustomOperation } from 'devextreme-react/filter-builder'
import axios from 'axios'
import DataGrid, { Column, Selection } from 'devextreme-react/data-grid'
import notify from '../../utils/notify'

interface FilterPopupProps {
	visible: boolean
	onClose: () => void
	onApply: (filter: any) => void
	modelName: string | null
	gridInstance: any
}

function normalizeFilter(filter: any): any {
	if (!Array.isArray(filter)) return filter
	if (typeof filter[0] === 'string' && (filter[1] === 'anyof' || filter[1] === 'noneof')) {
		const values = (filter[2] ?? []).map((v: any) => {
			while (Array.isArray(v)) v = typeof v[0] === 'string' && typeof v[1] === 'string' ? v[2] : v[0]
			return v
		})
		return [filter[0], filter[1], values]
	}
	return filter.map((item: any) => (Array.isArray(item) ? normalizeFilter(item) : item))
}

const SaveFilterPopup: React.FC<FilterPopupProps> = ({ visible, onClose, onApply, modelName, gridInstance }) => {
	const [savedFilters, setSavedFilters] = useState<any[]>([])
	const [filterName, setFilterName] = useState<string>('')
	const [localFilterValue, setLocalFilterValue] = useState<any>(null)

	const filterBuilderFields = (gridInstance?.getVisibleColumns() ?? [])
		.filter((col: any) => col.dataField)
		.map((col: any) => ({
			dataField: col.dataField,
			caption: col.caption ?? col.dataField,
			dataType: col.dataType,
			lookup: col.lookup,
			format: col.dataType === 'date' || col.dataType === 'datetime' ? 'dd/MM/yyyy' : col.format,
		}))

	const normalizedFilter = normalizeFilter(localFilterValue)
	const filterBuilderKey = localFilterValue ? JSON.stringify(localFilterValue) : 'empty'

	const fetchSavedFilters = useCallback(async () => {
		try {
			const response = await axios.get(`${API_FIBER_URL}/filters?model=${modelName}`)
			setSavedFilters(response.data.filters)
		} catch {
			notify('Failed to fetch the filters', 'error')
		}
	}, [modelName])

	const handleShowing = useCallback(() => {
		setLocalFilterValue(gridInstance?.option('filterValue') ?? null)
		fetchSavedFilters()
	}, [gridInstance, fetchSavedFilters])

	const deleteRow = useCallback(async (filterId: string) => {
		try {
			await axios.delete(`${API_FIBER_URL}/filters/delete/${filterId}`)
			setSavedFilters(prev => prev.filter(f => f.id !== filterId))
			notify('Filter deleted successfully', 'success')
		} catch {
			notify('Error deleting filter', 'error')
		}
	}, [])

	const handleSave = useCallback(async () => {
		try {
			await axios.post(`${API_FIBER_URL}/filters/save`, {
				name: filterName,
				statement: JSON.stringify(localFilterValue),
				model: modelName,
			})
			notify('Filter saved successfully', 'success')
			fetchSavedFilters()
		} catch {
			notify('Error saving filter', 'error')
		}
		setFilterName('')
	}, [filterName, localFilterValue, modelName, fetchSavedFilters])

	const handleApply = useCallback(
		(filterCriteria: any) => {
			const filter = typeof filterCriteria === 'string' ? JSON.parse(filterCriteria) : filterCriteria
			onApply(filter)
			onClose()
		},
		[onApply, onClose]
	)

	return (
		<Popup
			visible={visible}
			showTitle={true}
			title="Create Filter Statement"
			onHiding={onClose}
			defaultWidth="55%"
			defaultHeight="85%"
			hideOnOutsideClick={true}
			resizeEnabled={true}
			onShowing={handleShowing}
		>
			<div className="saveFilterInput">
				<TextBox
					value={filterName}
					onValueChanged={e => setFilterName(e.value)}
					valueChangeEvent="input"
					placeholder="Enter filter name"
					disabled={!localFilterValue}
					style={{ zoom: 0.9 }}
				/>
				<Button
					text="Save"
					onClick={handleSave}
					disabled={!localFilterValue || !filterName}
					style={{ zoom: 0.8 }}
				/>
			</div>
			<div>
				<p>
					<b>Current Active Filter:</b>
				</p>
				<FilterBuilder
					key={filterBuilderKey}
					fields={filterBuilderFields}
					value={normalizedFilter}
					disabled={true}
				>
					<CustomOperation
						name="anyof"
						caption="Is any of"
						dataTypes={['string', 'number', 'boolean', 'date', 'datetime', 'object']}
					/>
					<CustomOperation
						name="noneof"
						caption="Is none of"
						dataTypes={['string', 'number', 'boolean', 'date', 'datetime', 'object']}
					/>
				</FilterBuilder>
				{!localFilterValue && <p>Please create a filter from the data grid to proceed.</p>}
			</div>
			<br />
			<DataGrid dataSource={savedFilters} rowAlternationEnabled={true} showBorders={true} width="100%">
				<Selection mode="single" />
				<Column dataField="filter_name" width={200} caption="Saved Filters" />
				<Column
					dataField="check"
					caption=""
					width={200}
					cellRender={({ data }) => (
						<Button text="Apply filter" icon="check" onClick={() => handleApply(data.filter_criteria)} />
					)}
				/>
				<Column
					dataField="delete"
					caption=""
					width={10}
					cellRender={({ data }) => <Button icon="remove" onClick={() => deleteRow(data.id)} />}
				/>
			</DataGrid>
		</Popup>
	)
}

export default SaveFilterPopup
